import { useMemo } from 'react';
import { usePermissions } from '../contexts/PermissionContext';

/**
 * @description Hook for filtering menu items based on user permissions
 *
 * Key features:
 * - Filters arrays of menu items by permission codes
 * - Returns only items the user has permission to see
 * - Memoized for performance
 * - Handles loading state gracefully
 *
 * @dependencies
 * - PermissionContext: For permission checking
 *
 * @usage
 * const menuItems = [
 *   { label: 'New Product', permission: 'products.create', onClick: () => {} },
 *   { label: 'New Category', permission: 'categories.create', onClick: () => {} },
 *   { label: 'Help', onClick: () => {} }, // No permission = always visible
 * ];
 * const visibleItems = usePermissionFilteredItems(menuItems);
 *
 * @param {Array} items - Array of objects with optional `permission` property
 * @returns {Array} Filtered array containing only permitted items
 */
export const usePermissionFilteredItems = (items) => {
  const { hasPermission, isLoaded } = usePermissions();

  return useMemo(() => {
    // Return empty array while loading to prevent flash of unauthorized content
    if (!isLoaded) return [];

    // Filter items based on permission
    return items.filter(item => {
      // If no permission required, always show
      if (!item.permission) return true;

      // Handle single permission (string)
      if (typeof item.permission === 'string') {
        return hasPermission(item.permission);
      }

      // Handle array of permissions (OR logic - any permission grants access)
      if (Array.isArray(item.permission)) {
        return item.permission.some(p => hasPermission(p));
      }

      // Unknown permission format, default to showing
      return true;
    });
  }, [items, hasPermission, isLoaded]);
};

export default usePermissionFilteredItems;
